/**
 * Utility functions and type guards for InputFormula AST manipulation
 */

import locales from '../../../../../locales/employer-rules'
import {
  InputFormula,
  InputFormulaPath,
  InputFormulaOperation,
  InputFormulaRule,
  InputFormulaEventPath,
} from '../../../api/employer-rules'
import { PathOption } from './types'

/** Type guard to check if a formula is a path */
export const isInputFormulaPath = (formula: InputFormula): formula is InputFormulaPath => {
  return 'type' in formula && formula.type === 'path'
}

/** Type guard to check if a formula is an operation */
export const isInputFormulaOperation = (
  formula: InputFormula,
): formula is InputFormulaOperation => {
  return 'op' in formula
}

/** Type guard to check if a formula is a rule reference */
export const isInputFormulaRule = (formula: InputFormula): formula is InputFormulaRule => {
  return 'type' in formula && formula.type === 'rule'
}

/** Type guard to check if a formula is an eventPath reference */
export const isInputFormulaEventPath = (
  formula: InputFormula,
): formula is InputFormulaEventPath => {
  return 'type' in formula && formula.type === 'eventPath'
}

/**
 * Converts a simple path string to InputFormula format.
 * Used for backward compatibility.
 */
export const pathStringToFormula = (path: string): InputFormulaPath => {
  return {
    type: 'path',
    path,
  }
}

/**
 * Gets available path options with labels from locales based on period/mode.
 * Returns options with value (path) and label (from locales).
 */
export const getAvailablePathsForContext = (period: string, mode?: string): PathOption[] => {
  if (mode === 'timeRange') {
    return []
  }

  if (period === 'week') {
    return Object.keys(locales.inputs.week).map(key => ({
      value: key,
      label: locales.inputs.week[key as keyof typeof locales.inputs.week] as string,
    }))
  }

  if (period === 'day') {
    return Object.keys(locales.inputs.day).map(key => ({
      value: key,
      label: locales.inputs.day[key as keyof typeof locales.inputs.day] as string,
    }))
  }

  return []
}

/** Gets just the path values (for validation) */
export const getAvailablePathValuesForContext = (period: string, mode?: string): string[] => {
  return getAvailablePathsForContext(period, mode).map(option => option.value)
}
